import { getReportFooterText } from '../reportFooter';
import type { PersonLabels } from '@/hooks/usePersonLabels';
export interface CostOfBusinessHeaderInfo {
  fromDate?: string | null;
  toDate?: string | null;
  reportDate?: string | null;
  totalCost?: number | null;
}

export interface CostOfBusinessItem {
  individual?: string | null;
  documentNumber?: string | null;
  lin?: string | null;
  nsn?: string | null;
  nomenclature: string;
  transactionTypeLabel: string;
  conditionCode?: string | null;
  issueStatus?: string | null;
  quantityIssued?: number | null;
  unitCost?: number | null;
  totalLineCost?: number | null;
}

export interface CostOfBusinessTemplateOptions {
  generatedAt: Date;
  header: CostOfBusinessHeaderInfo;
  items: CostOfBusinessItem[];
  isDisposal?: boolean;
  personLabels?: PersonLabels;
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const sanitizeText = (value: string | null | undefined): string => {
  const trimmed = value?.toString().trim();
  if (!trimmed) {
    return '—';
  }
  return escapeHtml(trimmed);
};

const formatCurrency = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '—';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(value);
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return sanitizeText(value);
  }
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

const formatDateTime = (value: Date): string =>
  new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(value);

const buildItemsTable = (items: CostOfBusinessItem[], isDisposal: boolean): string => {
  if (items.length === 0) {
    return `
      <tr>
        <td colspan="10" class="empty-row">
          No transactions matched the selected filters.
        </td>
      </tr>
    `;
  }

  return items
    .map(
      (item) => `
        <tr>
          <td>${sanitizeText(isDisposal ? item.documentNumber : item.individual)}</td>
          <td>${sanitizeText(item.lin)}</td>
          <td>${sanitizeText(item.nsn)}</td>
          <td>${sanitizeText(item.nomenclature)}</td>
          <td>${sanitizeText(item.transactionTypeLabel)}</td>
          <td>${sanitizeText(item.conditionCode)}</td>
          <td>${sanitizeText(item.issueStatus)}</td>
          <td class="numeric">${sanitizeText(
            item.quantityIssued !== null && item.quantityIssued !== undefined
              ? String(item.quantityIssued)
              : '',
          )}</td>
          <td class="numeric">${formatCurrency(item.unitCost ?? null)}</td>
          <td class="numeric">${formatCurrency(item.totalLineCost ?? null)}</td>
        </tr>
      `,
    )
    .join('');
};

export const buildCostOfBusinessReportHtml = ({
  generatedAt,
  header,
  items,
  isDisposal = false,
  personLabels,
}: CostOfBusinessTemplateOptions): string => {
  const pl = personLabels ?? { label: 'Individual', labelPlural: 'Individuals', lower: 'individual', lowerPlural: 'individuals', article: 'an' as const };
  const generatedLabel = formatDateTime(generatedAt);
  const fromDateLabel = header.fromDate ? formatDate(header.fromDate) : '—';
  const toDateLabel = header.toDate ? formatDate(header.toDate) : '—';
  const reportDateLabel = header.reportDate ? formatDate(header.reportDate) : '—';
  const totalCostLabel = formatCurrency(header.totalCost ?? null);
  const itemsTable = buildItemsTable(items, isDisposal);
  const firstColumnHeader = isDisposal ? 'Document Number' : pl.label;

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>Cost of Business Report</title>
    <style>
      :root {
        color-scheme: light;
      }

      * {
        box-sizing: border-box;
      }

      body {
        margin: 16px;
        font-family: 'Roboto', 'Helvetica Neue', Arial, sans-serif;
        color: #15263c;
        background: #ffffff;
      }

      header {
        margin-bottom: 16px;
      }

      .title {
        font-size: 24px;
        font-weight: 700;
        margin: 0;
        text-align: center;
      }

      .meta-row {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        margin-top: 12px;
      }

      .meta-block {
        min-width: 220px;
      }

      .meta-label {
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #5c6f85;
        margin-bottom: 2px;
      }

      .meta-value {
        font-size: 14px;
        color: #15263c;
      }

      .total-block {
        margin-top: 12px;
        font-size: 14px;
        font-weight: 600;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 16px;
        border: 1px solid #d7e3f4;
      }

      thead th {
        background: #e9f1ff;
        text-align: left;
        font-size: 12px;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        padding: 10px 12px;
        color: #4a5c72;
        border-bottom: 1px solid #d7e3f4;
      }

      tbody td {
        padding: 10px 12px;
        border-bottom: 1px solid #edf3fc;
        font-size: 14px;
        color: #1f2f45;
      }

      tbody tr:last-child td {
        border-bottom: none;
      }

      tbody tr:nth-child(even) td {
        background: #f9fbff;
      }

      .numeric {
        text-align: right;
        font-variant-numeric: tabular-nums;
      }

      .empty-row {
        text-align: center;
        font-style: italic;
        color: #6b7c92;
        padding: 24px 12px;
      }

      footer {
        margin-top: 24px;
        font-size: 12px;
        color: #5c6f85;
        text-align: center;
      }

      @media print {
        body {
          margin: 8mm;
        }
      }
    </style>
  </head>
  <body>
    <header>
      <h1 class="title">Cost of Business Report</h1>
      <div class="meta-row">
        <div class="meta-block">
          <div class="meta-label">From</div>
          <div class="meta-value">${fromDateLabel}</div>
        </div>
        <div class="meta-block">
          <div class="meta-label">To</div>
          <div class="meta-value">${toDateLabel}</div>
        </div>
        <div class="meta-block">
          <div class="meta-label">Report Date</div>
          <div class="meta-value">${reportDateLabel}</div>
        </div>
      </div>
      <div class="total-block">
        Total Cost: ${totalCostLabel}
      </div>
    </header>

    <section>
      <table>
        <thead>
          <tr>
            <th>${firstColumnHeader}</th>
            <th>LIN</th>
            <th>NSN</th>
            <th>Nomenclature</th>
            <th>Transaction Type</th>
            <th>Condition Code</th>
            <th>Issue Status</th>
            <th>Qty Issued</th>
            <th>Unit Cost</th>
            <th>Total Line Cost</th>
          </tr>
        </thead>
        <tbody>
          ${itemsTable}
        </tbody>
      </table>
    </section>

    <footer>
      Generated ${escapeHtml(generatedLabel)} · ${getReportFooterText()}
    </footer>
  </body>
</html>`;
};
